/*
  <faq-search>: client-side search for the FAQ page.

  A question matches when every word of the query matches a word of the
  question, its answer, its category or its extra search terms ("Suchbegriffe").
  A word matches when it
    - is equal, or the start of a word (from 3 letters: "rück" → "rückgabe"),
    - is part of a longer compound word (from 4 letters: "kleber" → "klebestreifen"),
    - has small typos (1 letter off for 4–7 letters, 2 from 8 letters),
    - or is a synonym from the list below (e.g. "rückgabe" → "zurücksenden").
  If no question matches all words, questions matching at least one word are shown.
  Umlauts, ß and upper/lower case are ignored.
*/
if (!customElements.get('faq-search')) {
  const normalize = (text) =>
    text
      .toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/®/g, ' ')
      .normalize('NFD').replace(/[̀-ͯ]/g, '');

  // Rough "sounds like" form so spelling variants meet: rückgabe/rukgabe, kleben/klebben …
  const simplify = (word) =>
    word.replace(/ue/g, 'u').replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ck/g, 'k')
      .replace(/ph/g, 'f').replace(/th/g, 't').replace(/dt/g, 't').replace(/(.)\1+/g, '$1');

  const tokenize = (text) => normalize(text).split(/[^a-z0-9]+/).filter((word) => word.length > 1);

  // Groups of words that mean the same thing for our customers.
  const SYNONYMS = [
    ['rueckgabe', 'zuruecksenden', 'zurueckschicken', 'retoure', 'retournieren', 'umtausch', 'umtauschen', 'widerruf', 'paket', 'return'],
    ['rueckzahlung', 'erstattung', 'erstatten', 'geld', 'refund', 'gutschrift'],
    ['kaputt', 'beschaedigt', 'defekt', 'schaden', 'gebrochen', 'damaged'],
    ['bezahlen', 'zahlung', 'zahlungsart', 'zahlungsarten', 'bezahlmoeglichkeiten', 'paypal', 'klarna', 'rechnung', 'kreditkarte', 'applepay', 'payment'],
    ['kleber', 'kleben', 'klebestreifen', 'klebeflaeche', 'verklebt', 'verkleben', 'aufkleben', 'klebung', 'adhesive'],
    ['trocknen', 'aushaerten', 'trockenzeit', 'warten', 'ruhen'],
    ['entfernen', 'abloesen', 'abmachen', 'loesen', 'rueckstaende', 'rueckstandsfrei', 'abnehmen', 'remove'],
    ['monitor', 'monitore', 'bildschirm', 'bildschirme', 'screen', 'screens', 'display', 'displays'],
    ['curved', 'gebogen', 'gewoelbt', 'krumm'],
    ['vertikal', 'uebereinander', 'hochkant', 'senkrecht'],
    ['anzahl', 'wieviele', 'menge', 'sets', 'triple', 'brauche'],
    ['montage', 'montieren', 'anbringen', 'installieren', 'befestigen', 'aufbau', 'anleitung'],
    ['mehrmals', 'wiederverwenden', 'wiederverwendbar', 'nochmal', 'erneut'],
    ['versand', 'lieferung', 'liefern', 'lieferzeit', 'shipping'],
    ['kontakt', 'support', 'hilfe', 'email', 'mail'],
  ];
  const synonymIndex = new Map();
  SYNONYMS.forEach((group) => group.forEach((word) => synonymIndex.set(word, group)));

  const distance = (a, b, max) => {
    // Damerau-Levenshtein (optimal string alignment) with early exit.
    if (Math.abs(a.length - b.length) > max) return max + 1;
    const rows = [];
    for (let i = 0; i <= a.length; i += 1) {
      rows[i] = [i];
      let rowMin = i;
      for (let j = 1; j <= b.length; j += 1) {
        if (i === 0) { rows[i][j] = j; continue; }
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let value = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) value = Math.min(value, rows[i - 2][j - 2] + 1);
        rows[i][j] = value;
        if (value < rowMin) rowMin = value;
      }
      if (i > 0 && rowMin > max) return max + 1;
    }
    return rows[a.length][b.length];
  };

  const allowedTypos = (word) => (word.length >= 8 ? 2 : word.length >= 4 ? 1 : 0);

  // Does a single query word match one of the item's words?
  const wordMatches = (query, words) => {
    const typos = allowedTypos(query);
    const simpleQuery = simplify(query);
    return words.some((word) => {
      if (word === query) return true;
      if (query.length >= 3 && word.startsWith(query)) return true;
      if (query.length >= 4 && word.includes(query)) return true;
      const simpleWord = simplify(word);
      if (simpleWord === simpleQuery) return true;
      if (simpleQuery.length >= 4 && simpleWord.startsWith(simpleQuery)) return true;
      if (!typos) return false;
      if (Math.abs(simpleWord.length - simpleQuery.length) <= typos && distance(simpleQuery, simpleWord, typos) <= typos) return true;
      // Typo in the beginning of a longer word, e.g. "rukgabr" vs "rückgabebedingungen"
      if (simpleWord.length > simpleQuery.length && distance(simpleQuery, simpleWord.slice(0, simpleQuery.length), typos) <= typos) return true;
      return false;
    });
  };

  // Expand a query word with its synonyms (also when the word itself has a typo).
  const variants = (query) => {
    const result = new Set([query]);
    const typos = allowedTypos(query);
    synonymIndex.forEach((group, key) => {
      const hit = key === query
        || (query.length >= 4 && key.startsWith(query))
        || simplify(key) === simplify(query)
        || (typos && distance(simplify(query), simplify(key), typos) <= typos);
      if (hit) group.forEach((word) => result.add(word));
    });
    return Array.from(result);
  };

  customElements.define('faq-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-faq-input]');
      this.empty = this.querySelector('[data-faq-empty]');
      this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
      this.items = Array.from(this.querySelectorAll('[data-faq-item]')).map((element) => {
        // The category title in front of the question counts as searchable text too.
        let group = element.previousElementSibling;
        while (group && !group.hasAttribute('data-faq-group')) group = group.previousElementSibling;
        const text = [element.textContent, element.dataset.keywords || '', group ? group.textContent : ''].join(' ');
        return { element, words: Array.from(new Set(tokenize(text))) };
      });
      let timer;
      this.input?.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.filter(), 120);
      });
    }

    filter() {
      const queryWords = tokenize(this.input.value);
      let results;
      if (!queryWords.length) {
        results = this.items.map((item) => ({ item, show: true }));
      } else {
        const expanded = queryWords.map(variants);
        const matchesWord = (item, options) => options.some((option) => wordMatches(option, item.words));
        results = this.items.map((item) => ({ item, show: expanded.every((options) => matchesWord(item, options)) }));
        // Nothing matches all words: show questions that match at least one.
        if (!results.some((result) => result.show) && expanded.length > 1) {
          results = this.items.map((item) => ({ item, show: expanded.some((options) => matchesWord(item, options)) }));
        }
      }

      let visible = 0;
      results.forEach(({ item, show }) => {
        item.element.hidden = !show;
        if (show) visible += 1;
      });

      this.groups.forEach((group) => {
        let next = group.nextElementSibling;
        let has = false;
        while (next && !next.hasAttribute('data-faq-group')) {
          if (next.hasAttribute('data-faq-item') && !next.hidden) has = true;
          next = next.nextElementSibling;
        }
        group.hidden = !has;
      });

      // With a search term, open the hits when there are only a few.
      if (queryWords.length) {
        results.forEach(({ item, show }) => { item.element.open = show && visible <= 3; });
      }
      if (this.empty) this.empty.hidden = visible > 0;
    }
  });
}
